import { Server } from "@modules/api";
import TranslatedText from "@components/items/display/TranslatedText";
import { ListeningIcon } from "@components/icons/ListeningIcon";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { Skeleton } from "@ui/skeleton";
import { OnItemPress } from "@modules/content/hooks/useComponentData";

export interface SkillContentVerticalListParameters {
  title: string | null | undefined;
}

export interface SkillContentVerticalListItemParameters {
  image: string | null | undefined;
}

type ItemParams = {
  id: string | undefined;
  title: string | undefined;
  authors: string[] | undefined;
} & {
  [K in keyof SkillContentVerticalListItemParameters]?:
    | SkillContentVerticalListItemParameters[K]
    | undefined;
};

interface SkillContentVerticalListProps {
  onItemPress: OnItemPress<ItemParams>;
  contents:
    | (Server.ContentEntityOutput & {
        isLocked: boolean | undefined;
        lockType: Server.LockType | undefined;
      })[]
    | undefined;
  isLoading: boolean;
  parameterValues: SkillContentVerticalListParameters;
  items: ItemParams[];
}

function SkillContentVerticalList({
  onItemPress,
  contents,
  items,
  parameterValues,
  isLoading,
}: SkillContentVerticalListProps) {
  const navigate = useAppNavigate();

  const isLoadingVisible = useIsLoadingVisibile(
    isLoading && (!contents || Boolean(contents && !contents.length))
  );

  return (
    <div
      className={`relative ${(isLoadingVisible || isLoading) && "h-[800px]"}`}
    >
      <div
        className={`w-full ${
          (isLoadingVisible || isLoading) &&
          "z-10 h-full bg-white dark:bg-black"
        } absolute top-0 px-10 ltr:left-0 rtl:right-0`}
      >
        <SkillContentVerticalListLoading isLoading={isLoadingVisible} />
      </div>
      {contents && contents.length ? (
        <div className="px-10 pb-[35px]">
          <div className="text-[20px] font-bold text-b2b-primary">
            {parameterValues.title}
          </div>
          <div className="mt-[45px] flex w-full flex-col items-start justify-start gap-3">
            {items.map((t, i) => {
              return (
                <div
                  key={t.id}
                  className="flex w-full max-w-[705px] cursor-pointer items-center justify-between rounded-[10px] border border-[#D9D9D9] px-4 py-4"
                  onClick={() => {
                    const content = contents.find((c) => c.id === t.id);

                    content &&
                      onItemPress(content, t, { isLocked: content.isLocked });
                  }}
                >
                  <div className="flex items-center justify-center gap-6">
                    <img
                      src={t.image || ""}
                      alt={t.title}
                      className="h-[56px] w-[56px] rounded-[8px]"
                    />
                    <div>
                      <div className="text-[11px] text-[#898A8D] opacity-70">
                        <span>
                          <TranslatedText
                            className="inline"
                            text="skill-details.card.summary"
                          />
                        </span>{" "}
                        {i + 1}
                      </div>
                      <div className="text-[15px] text-black dark:text-white">
                        {t.title}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <div className="text-black dark:text-white">
                      <ListeningIcon width={20} height={18} />
                    </div>
                    <div>
                      <img
                        src="/assets/icons/reading.svg"
                        alt="reading"
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const SkillContentVerticalListLoading = ({
  isLoading,
}: {
  isLoading: boolean;
}) => {
  const skeletonData: { id: string }[] = Array.from({ length: 8 }).map(
    (s, i) => {
      return {
        id: i + "",
      };
    }
  );

  const renderItems = () => {
    return skeletonData.map((s) => {
      return (
        <div key={s.id}>
          <Skeleton
            className={`h-[90px] w-[700px] rounded-[12px] bg-[#f4f4f5] dark:bg-[#27272a]`}
          ></Skeleton>
        </div>
      );
    });
  };

  return isLoading ? (
    <div className="mt-[45px] flex w-full flex-col items-start justify-start gap-y-2">
      {renderItems()}
    </div>
  ) : null;
};

export default SkillContentVerticalList;
